import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import {useAppDispatch} from "../../../../redux";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {createUUId} from "../../../../../core/services/utils/utils";
import {setReduxDialogState} from "../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../core/models/static/routes/redux-store-routes";
import {Box, Card, CircularProgress, Skeleton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import Button from "../../../base/button/button";
import {AddCircleOutline, DeleteOutlined, EditOutlined, WarningOutlined} from "@mui/icons-material";
import ValidateMessages from "../../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from "../../../form";
import IconButton from "../../../base/button/icon_button";
import SaveOrRollback from "../../save-or-rollback";
import {ProductDimensionUnit, useSelectedDimensionMetrics} from "../../manage-product-dimensions/unit-management";
import Api from "../../../../../core/services/api/api";
import {computeContainerWeightMetrics} from "./dimensions-utils";

export const OrderDimensionsSectionContainerInfoManagement = ({
                                                                  containerType: containerTypeProps,
                                                                  setContainerType: setContainerTypeProps,
                                                                  replaceContainerWith,
                                                                  containers,
                                                                  setContainers,
                                                                  loading,
                                                                  containerTypeList,
                                                                  currentContainerTypeTitle,
                                                                  getContainerTitle,
                                                                  orderId,
                                                                  invoiceId,
                                                                  mergedParts,
                                                              }) => {
    const [updatingIds, setUpdatingIds] = useState([]);
    const [removingIds, setRemovingIds] = useState([]);
    const [containerType, setContainerType] = useState(containerTypeProps);
    const [updatingContainerType, setUpdatingContainerType] = useState(false);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();
    const operatorAccessLevels = useAccessLevels();
    const scrollerRef = useRef(null);

    const imputedContainers = useMemo(
        () => computeContainerWeightMetrics(containers, mergedParts),
        [containers, mergedParts],
    );


    /**
     * Sync the container type prop to state
     */
    useLayoutEffect(() => {
        setContainerType(containerTypeProps);
    }, [containerTypeProps]);

    /**
     * The sx style mixin for the condition's toggle button group.
     */
    const toggleButtonGroupMixin = useCallback((theme) => {
        return ({
            border: `1px solid rgba(var(--palette-common-onBackgroundChannel) / 0.23)`,
            width: '100%',
            '& .MuiToggleButton-root': {
                backgroundColor: 'transparent',
                borderColor: 'transparent !important',
                color: theme.palette.text.primary,
                height: 30,
                marginBlock: 0.5,
                marginInline: 0.5,
                padding: 1,
                width: '50%',
            },
            '& .MuiToggleButton-root:hover': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.text.primary,
            },
            '& .MuiToggleButton-root.Mui-selected': {
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
                color: theme.palette.primary.main,
            },
            '& .MuiToggleButton-root.Mui-selected:hover': {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.background.tertiary,
                borderColor: `${theme.palette.background.tertiary} !important`,
            },
        })
    }, []);

    /**
     * Update the container type
     */
    const updateContainerType = useCallback(async (newContainerType) => {
        if (!newContainerType)
            return;
        setContainerType(newContainerType);
        setUpdatingContainerType(true);
        /**@type {(ApiResponseDs<unknown | null> | undefined)[]}**/
        const responses = await Promise.all(
            containers.map(container => Api.updateOrderPallet({
                id: container.id,
                type: newContainerType,
                updatedProperties: ["Type"],
            }))
        );
        if (!isMounted())
            return;
        if (!responses.some(r => r?.resultFlag)) {
            setContainerType(containerType);
        } else {
            // update the parent's container type prop so that it propogates back.
            setContainerTypeProps(newContainerType);
            for (const response of responses) {
                if (response.resultFlag) {
                    setContainers(prevContainers => prevContainers?.map(c => response.data.id === c.id ? ({...c, type: newContainerType,}) : c) ?? []);
                }
            }
        }
        setUpdatingContainerType(false);
    }, [containers, isMounted, containerType, setContainerTypeProps, setContainers]);

    /**
     * Add a new container
     * @type {(function(): void)}
     */
    const addNewContainer = useCallback(() => {
        const newContainer = {
            uniqueId: createUUId(true),
        };
        setContainers(prevContainers => [...(prevContainers ?? []), newContainer]);
        // Scroll to the end of the scroller
        setTimeout(() => {
            if (scrollerRef.current) {
                scrollerRef.current.scrollTo({
                    left: scrollerRef.current.scrollWidth,
                    behavior: 'smooth',
                });
            }
        }, 100);
    }, [setContainers]);

    /**
     * Update a container in the server and update the containers list
     * @type {(function(*): void)|*}
     */
    const updateContainer = useCallback(async (container) => {
        setUpdatingIds(prev => [...prev, container.uniqueId]);
        const response = await Api.updateOrderPallet({
            id: container.id,
            title: container.title?.trim() ? container.title.trim() : null,
            height: container.height,
            width: container.width,
            length: container.length,
            netWeight: container.netWeight,
            type: container.type,
            updatedProperties: ["Title", "Height", "Width", "Length", "NetWeight"],
        })
        if (!isMounted())
            return;
        if (response.resultFlag) {
            setContainers(prevContainers => prevContainers?.map(c => c.uniqueId === container.uniqueId ? {...container, ...response.data} : c) ?? []);
        }
        setUpdatingIds(prev => prev.filter(id => id !== container.uniqueId));
        return response?.resultFlag ?? false;
    }, [isMounted, setContainers]);

    /**
     * Create a container in the server and add it to the containers list
     * @type {(function(*): Promise<void>)|*}
     */
    const createContainer = useCallback(async (container) => {
        setUpdatingIds(prev => [...prev, container.uniqueId]);
        const response = await Api.createOrderPallet({
            title: container.title?.trim() ? container.title.trim() : null,
            height: container.height,
            width: container.width,
            length: container.length,
            netWeight: container.netWeight,
            type: containerType,
            orderId: orderId,
            invoiceId: invoiceId,
        })
        if (!isMounted())
            return;
        if (response.resultFlag) {
            setContainers(prevContainers => prevContainers?.map(c => c.uniqueId === container.uniqueId ? {
                ...container,
                ...response.data,
            } : c) ?? []);
        }
        setUpdatingIds(prev => prev.filter(id => id !== container.uniqueId));
        return response?.resultFlag ?? false
    }, [containerType, invoiceId, isMounted, orderId, setContainers]);

    /**
     * Remove a container from the server and the containers list
     * @type {(function(*): Promise<void>)}
     */
    const removeContainer = useCallback(async (container) => {
        if (!container.id) {
            // Just remove it from the list
            setContainers(prevContainers => prevContainers?.filter(c => c.uniqueId !== container.uniqueId) ?? []);
            return;
        }
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: containers
                .filter(e => e.id !== container.id && !!e.id)
                .map(e => ({
                    title: getContainerTitle(e),
                    id: e.id,
                    data: e,
                })),
            removedEntryTitle: getContainerTitle(container),
            onClose: async (confirmed, data) => {
                if (!confirmed)
                    return;
                if (!data)
                    return;
                const replacement = containers?.find(c => c.id === data.id);
                if (!replacement)
                    return;
                setRemovingIds(prev => [...prev, container.uniqueId]);
                const response = await Api.deleteOrderPallet({id: container.id, replacementId: replacement.id});
                if (!isMounted())
                    return;
                if (response.resultFlag) {
                    setContainers(prevContainers => prevContainers?.filter(c => c.uniqueId !== container.uniqueId) ?? []);
                    replaceContainerWith(container, replacement);
                }
                setRemovingIds(prev => prev.filter(id => id !== container.uniqueId));
                return response?.resultFlag ?? false
            }
        }));
    }, [containers, dispatch, getContainerTitle, isMounted, replaceContainerWith, setContainers]);

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
                    <Box sx={{display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2}}>
                        <Typography variant={'body1'} fontWeight={600} flexShrink={0}>
                            Pallet Info
                        </Typography>
                        {
                            !loading &&
                            <Box sx={{width: 220}}>
                                <ToggleButtonGroup
                                    orientation="horizontal"
                                    value={containerType}
                                    exclusive
                                    onChange={(e, containerType) => operatorAccessLevels.packages.sales.manageSalesPallets && updateContainerType(containerType)}
                                    sx={toggleButtonGroupMixin}
                                    disabled={updatingContainerType}
                                >
                                    {
                                        containerTypeList.map((type) => (
                                            <ToggleButton
                                                value={type.value} key={type.value} disabled={updatingContainerType}
                                                sx={{display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'center', alignItems: 'center'}}>
                                                {
                                                    updatingContainerType && containerType === type.value
                                                        ? <CircularProgress size={20} sx={{flexShrink: 0}}/>
                                                        : null
                                                }
                                                <Typography variant={'overline'} fontWeight={600}>
                                                    {type.title ?? ""}
                                                </Typography>
                                            </ToggleButton>
                                        ))
                                    }
                                </ToggleButtonGroup>
                            </Box>
                        }
                    </Box>
                    {
                        operatorAccessLevels.packages.sales.manageSalesPallets &&
                        <Button
                            startIcon={<AddCircleOutline/>}
                            onClick={addNewContainer}
                            disabled={loading}
                            variant="outlined"
                            color="primary"
                            size={'medium'}
                            sx={{mt: 1}}
                        >
                            {loading ? "Loading..." : `Add New ${currentContainerTypeTitle}`}
                        </Button>
                    }
                </Box>
                <Box ref={scrollerRef} sx={{display: 'flex', width: '100%', gap: 1, alignItems: 'flex-start', justifyContent: 'flex-start', overflowX: 'auto', paddingBottom: 1}}>
                    {
                        loading &&
                        Array.from({length: 4}, (_, i) => (
                            <Skeleton key={`loading-${i}`} width={240} height={175} sx={{transform: 'none'}}/>
                        ))
                    }
                    {
                        !loading &&
                        imputedContainers?.map((container) => (
                            <ContainerManagementCard
                                key={container.id}
                                disableRemoving={containers.filter(e => !!e.id).length <= 1}
                                container={container}
                                removing={removingIds.includes(container.uniqueId)}
                                updating={updatingIds.includes(container.uniqueId)}
                                updateContainer={updateContainer}
                                createContainer={createContainer}
                                removeContainer={removeContainer}
                                getContainerTitle={getContainerTitle}
                            />
                        ))
                    }
                    {
                        !loading && containers?.length === 0 &&
                        <Box sx={{width: '100%', marginBlock: 10}}>
                            <Typography variant={'body2'} color={'text.secondary'} textAlign={'center'}>
                                No {currentContainerTypeTitle.toLowerCase()}s added yet. You can add one using the button above.
                            </Typography>
                        </Box>
                    }
                </Box>
            </Box>
        </>
    );

}
const ContainerManagementCard = ({
                                     container,
                                     updating,
                                     removing,
                                     updateContainer,
                                     createContainer,
                                     removeContainer,
                                     disableRemoving,
                                     getContainerTitle
                                 }) => {
    const {lengthUnit, weightUnit, transformLengthValue, transformWeightValue} = useSelectedDimensionMetrics();
    const [_inEditMode, setInEditMode] = useState(false);
    const isTemporary = useMemo(() => !container.id, [container]);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();

    const inEditMode = useMemo(() => (isTemporary ? true : _inEditMode) && operatorAccessLevels.packages.sales.manageSalesPallets, [_inEditMode, isTemporary, operatorAccessLevels.packages.sales.manageSalesPallets]);

    const schema = useMemo(() => Yup.object().shape({
        title: Yup.string().nullable(),
        length: Yup.number().moreThan(0, ValidateMessages.greaterThan('0')).required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')).nullable(),
        width: Yup.number().moreThan(0, ValidateMessages.greaterThan('0')).required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')).nullable(),
        height: Yup.number().moreThan(0, ValidateMessages.greaterThan('0')).required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')).nullable(),
        netWeight: Yup.number().moreThan(0, ValidateMessages.greaterThan('0')).required(ValidateMessages.required).typeError(ValidateMessages.incorrectType('number')).nullable(),
    }), []);

    const initialValues = useMemo(() => ({
        title: container.title ?? '',
        length: container.length ? transformLengthValue(container.length, {fromDefault: true, displayDecimals: 5}) : '',
        width: container.width ? transformLengthValue(container.width, {fromDefault: true, displayDecimals: 5}) : '',
        height: container.height ? transformLengthValue(container.height, {fromDefault: true, displayDecimals: 5}) : '',
        netWeight: container.netWeight ? transformWeightValue(container.netWeight, {fromDefault: true, displayDecimals: 5}) : '',
    }), [container.title, container.length, container.width, container.height, container.netWeight, transformLengthValue, transformWeightValue]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Handle the form submission
     * @type {(function(*): Promise<void>)}
     */
    const onSubmit = useCallback(async (values) => {
        const updatedContainer = {
            ...container,
            title: values.title ?? "",
            height: Number(transformLengthValue(Number(values.height), {toDefault: true, displayDecimals: 2})),
            width: Number(transformLengthValue(Number(values.width), {toDefault: true, displayDecimals: 2})),
            length: Number(transformLengthValue(Number(values.length), {toDefault: true, displayDecimals: 2})),
            netWeight: Number(transformWeightValue(Number(values.netWeight), {toDefault: true, displayDecimals: 2})),
        };
        let successful;
        if (isTemporary) {
            successful = await createContainer(updatedContainer);
        } else {
            successful = await updateContainer(updatedContainer);
        }
        if (!isMounted())
            return;
        if (successful) {
            setInEditMode(false);
        }
    }, [container, transformLengthValue, transformWeightValue, isTemporary, isMounted, createContainer, updateContainer]);

    /**
     * Revert the form changes
     * @type {(function(*): void)}
     */
    const revertChanges = useCallback((form) => {
        if (isTemporary) {
            removeContainer(container);
            return;
        }
        form.reset({
            title: container.title ?? '',
            length: container.length ? transformLengthValue(container.length, {fromDefault: true, displayDecimals: 5}) : '',
            width: container.width ? transformLengthValue(container.width, {fromDefault: true, displayDecimals: 5}) : '',
            height: container.height ? transformLengthValue(container.height, {fromDefault: true, displayDecimals: 5}) : '',
            netWeight: container.netWeight ? transformWeightValue(container.netWeight, {fromDefault: true, displayDecimals: 5}) : '',
        })
        form.getRegisteredFields().forEach((name) => {
            form.resetFieldState(name);
        })
        setInEditMode(false);
    }, [container, isTemporary, removeContainer, transformLengthValue, transformWeightValue]);


    return (
        <>
            <Form onSubmit={onSubmit} validate={validate} initialValues={initialValues} formProps={{style: {height: '100%'}}}>
                {({form, values}) => {
                    const hasNetWeight = values.netWeight !== '' && values.netWeight !== null && values.netWeight !== undefined && Number(values.netWeight) > 0;
                    const tareWeight = hasNetWeight && container.grossWeight ? Math.max(0, Number(values.netWeight) - container.grossWeight) : null;

                    const weightComplementaryInformation = (
                        hasNetWeight &&
                        <Box sx={{width: '100%'}}>
                            <Stack gap={0} direction={'column'}>
                                <Typography
                                    component={Box}
                                    variant={'body2'}
                                    color={container.hasImputedWeight ? 'warning.main' : 'text.secondary'}
                                    sx={{display: 'flex', alignItems: 'center', gap: 1}}
                                >
                                    Gross Weight:
                                    <Typography component={'span'} variant={'body2'} color={'text.primary'}>
                                        {transformWeightValue(container.grossWeight, {fromDefault: true, displayDecimals: 2})}
                                    </Typography>
                                    <ProductDimensionUnit unit={weightUnit}/>
                                    {
                                        container.hasImputedWeight &&
                                        <Tooltip
                                            title={"Gross weight is calculated based on the net weight of the assigned parts. One or more assigned parts are missing net weight information, so the gross weight may be inaccurate."}>
                                            <WarningOutlined/>
                                        </Tooltip>
                                    }
                                </Typography>
                                <Typography
                                    component={Box}
                                    variant={'body2'}
                                    color={container.hasImputedWeight ? 'warning.main' : 'text.secondary'}
                                    sx={{display: 'flex', alignItems: 'center', gap: 1}}
                                >
                                    Tare Weight:
                                    <Typography component={'span'} variant={'body2'} color={'text.primary'}>
                                        {transformWeightValue(tareWeight, {fromDefault: true, displayDecimals: 2})}
                                    </Typography>
                                    <ProductDimensionUnit unit={weightUnit}/>
                                    {
                                        container.hasImputedWeight &&
                                        <Tooltip
                                            title={"Tare weight is calculated based on the gross weight and the net weight. The gross weight may be inaccurate due to missing net weight information of assigned parts, so the tare weight may also be inaccurate."}>
                                            <WarningOutlined/>
                                        </Tooltip>
                                    }
                                </Typography>
                            </Stack>
                        </Box>
                    )


                    return (
                        <Card variant={'outlined'} sx={{padding: 2, width: 240, minHeight: 120, height: '100%', position: 'relative', opacity: updating || removing ? 0.6 : 1}}>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    {
                                        inEditMode &&
                                        <MuiRFF.TextField
                                            name={'title'}
                                            label={'Title'}
                                            fullWidth
                                            required={required.title}
                                            disabled={updating || removing}
                                            size={'small'}
                                        />
                                    }
                                    {
                                        !inEditMode &&
                                        <Typography variant={'body1'} fontWeight={600}>
                                            {getContainerTitle(container)}
                                        </Typography>
                                    }
                                    {
                                        !inEditMode && operatorAccessLevels.packages.sales.manageSalesPallets &&
                                        <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexShrink: 0}}>
                                            <IconButton
                                                size={'small'}
                                                onPressed={() => setInEditMode(true)}
                                                color={'paper.secondary'}
                                                disabled={updating || removing}
                                            >
                                                {
                                                    updating
                                                        ? <CircularProgress size={20}/>
                                                        : <EditOutlined/>
                                                }
                                            </IconButton>
                                            {
                                                (!disableRemoving || isTemporary) &&
                                                <IconButton size={'small'} onPressed={() => removeContainer(container)} color={'paper.error'} disabled={updating || removing}>
                                                    {
                                                        removing
                                                            ? <CircularProgress size={20}/>
                                                            : <DeleteOutlined/>
                                                    }
                                                </IconButton>
                                            }
                                        </Box>
                                    }
                                </Box>
                                {
                                    inEditMode &&
                                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                        <MuiRFF.TextField
                                            name={'length'}
                                            label={'Length'}
                                            fullWidth
                                            required={required.length}
                                            disabled={updating || removing}
                                            size={'small'}
                                            InputProps={{
                                                endAdornment: <ProductDimensionUnit unit={lengthUnit} inInput/>,
                                            }}
                                        />
                                        <MuiRFF.TextField
                                            name={'width'}
                                            label={'Width'}
                                            fullWidth
                                            required={required.width}
                                            disabled={updating || removing}
                                            size={'small'}
                                            InputProps={{
                                                endAdornment: <ProductDimensionUnit unit={lengthUnit} inInput/>,
                                            }}
                                        />
                                        <MuiRFF.TextField
                                            name={'height'}
                                            label={'Height'}
                                            fullWidth
                                            required={required.height}
                                            disabled={updating || removing}
                                            size={'small'}
                                            InputProps={{
                                                endAdornment: <ProductDimensionUnit unit={lengthUnit} inInput/>,
                                            }}
                                        />
                                        <MuiRFF.TextField
                                            name={'netWeight'}
                                            label={'Net Weight'}
                                            fullWidth
                                            required={required.netWeight}
                                            disabled={updating || removing}
                                            size={'small'}
                                            InputProps={{
                                                endAdornment: <ProductDimensionUnit unit={weightUnit} inInput/>,
                                            }}
                                        />
                                        {weightComplementaryInformation}
                                    </Box>
                                }
                                {
                                    !inEditMode &&
                                    <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                        <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                            <Typography variant={'body2'} fontWeight={400} component={'span'}>length:</Typography>
                                            {
                                                !!container.length
                                                    ? <>{transformLengthValue(container.length, {fromDefault: true})} <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></>
                                                    : 'N/A'
                                            }
                                        </Typography>
                                        <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                            <Typography variant={'body2'} fontWeight={400} component={'span'}>Width:</Typography>
                                            {
                                                !!container.width
                                                    ? <>{transformLengthValue(container.width, {fromDefault: true})} <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></>
                                                    : 'N/A'
                                            }
                                        </Typography>
                                        <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                            <Typography variant={'body2'} fontWeight={400} component={'span'}>Height:</Typography>
                                            {
                                                !!container.height
                                                    ? <>{transformLengthValue(container.height, {fromDefault: true})} <ProductDimensionUnit unit={lengthUnit} backgroundColor={"transparent"}/></>
                                                    : 'N/A'
                                            }
                                        </Typography>
                                        <Typography variant={'body2'} fontWeight={600} sx={{display: 'flex', gap: 0.5, alignItems: 'center'}} component='div'>
                                            <Typography variant={'body2'} fontWeight={400} component={'span'}>Net Weight:</Typography>
                                            {
                                                !!container.netWeight
                                                    ? <>{transformWeightValue(container.netWeight, {fromDefault: true})} <ProductDimensionUnit unit={weightUnit} backgroundColor={"transparent"}/></>
                                                    : 'N/A'
                                            }
                                        </Typography>
                                        {weightComplementaryInformation}
                                    </Box>
                                }
                                {
                                    inEditMode &&
                                    <Box sx={{width: '100%'}}>
                                        <SaveOrRollback
                                            show
                                            showText
                                            column={false}
                                            onSave={() => form.submit()}
                                            onRollback={() => revertChanges(form)}
                                            loading={updating || removing}
                                        />
                                    </Box>
                                }
                            </Box>
                        </Card>
                    );
                }}
            </Form>
        </>
    );
}